import {
  BoldEnum,
  Button,
  Heading,
  HeadingEnum,
  FilterChipsList,
  Coupon,
  SideSheet,
  Input,
  TextColorEnum,
  ImageSizeEnum,
  Image,
  LabelEnum,
  CouponsRedemptionCard,
  TextEnum,
  Text,
} from '@desquared/wave-components-library';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState, useContext, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { addCoupon, getCoupons, getCouponsStats } from '@/api/wave/users';
import { Controller, useForm } from 'react-hook-form';
import CouponAvocado from '../../../public/assets/wave-grocery/CouponAvocado.png';
import styles from './CouponsWalletPage.module.css';
import { QRCodeSVG } from 'qrcode.react'; //
import ReactModal from 'react-modal';
import { daysUntilExpires, formatExpiresDate } from '@/utils/dates/dates.js';
import { CurrencyContext } from '@/contexts/CurrencyProvider';
import CouponsSideSheetEmptyState from '../../../public/assets/wave-grocery/Empty-States-Default/CouponsSideSheetEmptyState.png';
import ExpiredCouponsEmptyState from '../../../public/assets/wave-grocery/Empty-States-Default/ExpiredCouponsEmptyState.png';
import RedeemedCouponsEmptyState from '../../../public/assets/wave-grocery/Empty-States-Default/RedeemedCouponsEmptyState.png';
import { useToast } from '@/contexts/ToastContext';

const customStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 9999,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    background: 'white',
    border: 'none',
    borderRadius: '0.5rem',
    padding: '1rem 2rem 2rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '90vw',
    maxHeight: '90vh',
  },
};

export function CouponsWalletPage({ user }) {
  const t = useTranslations();
  const { locale } = useRouter();
  const queryClient = useQueryClient();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [code, setCode] = useState('');
  const { showToast } = useToast();
  const [newlyAddedCode, setNewlyAddedCode] = useState(null);
  const openModal = () => setModalIsOpen(true);
  const closeModal = () => setModalIsOpen(false);
  const [selectedFilterId, setSelectedFilterId] = useState('active');
  const filters = useMemo(
    () => [
      {
        _id: 'active',
        selected: selectedFilterId === 'active',
        text: t('coupons.active', { count: 10 }),
      },
      {
        _id: 'redeemed',
        selected: selectedFilterId === 'redeemed',
        text: t('coupons.redeemed', { count: 10 }),
      },
      {
        _id: 'expired',
        selected: selectedFilterId === 'expired',
        text: t('coupons.expired', { count: 10 }),
      },
    ],
    [t, selectedFilterId]
  );
  const statusesMap = {
    active: LabelEnum.Warning,
    redeemed: LabelEnum.Open,
    expired: LabelEnum.Close,
  };
  const emptyStateImageMap = {
    active: CouponsSideSheetEmptyState.src,
    redeemed: RedeemedCouponsEmptyState.src,
    expired: ExpiredCouponsEmptyState.src,
  };
  const emptyStateTextMap = {
    active: t('coupons.empty_active'),
    redeemed: t('coupons.empty_redeemed'),
    expired: t('coupons.empty_expired'),
  };
  const emptyStateSubTextMap = {
    active: t('coupons.empty_active_sub'),
    redeemed: t('coupons.empty_redeemed_sub'),
    expired: t('coupons.empty_expired_sub'),
  };
  const { formatter, symbol } = useContext(CurrencyContext);

  const [isAddCouponSidesheetOpen, setIsAddCouponSideSheetOpen] = useState(false);

  const selectedStatus = useMemo(() => filters.find(f => f.selected)?._id, [filters]);
  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors, isValid, isSubmitting },
  } = useForm({
    mode: 'onChange',
    defaultValues: { code: '' },
  });

  useEffect(() => {
    if (isAddCouponSidesheetOpen) {
      reset({ code: '' });
      clearErrors();
    }
  }, [isAddCouponSidesheetOpen, reset, clearErrors]);

  const addCouponMutation = useMutation({
    mutationFn: payload => addCoupon(user.token, payload),
    onSuccess: (data, variables) => {
      setNewlyAddedCode(variables.code);
      queryClient.invalidateQueries({ queryKey: ['user', 'coupons', locale] });
      showToast(t('coupons.sidesheet.added_coupon'));
      setIsAddCouponSideSheetOpen(false);
    },
    onError: error => {
      const status = error?.response?.status;

      if (status === 404) {
        setError('code', {
          type: 'server',
          message: t('coupons.sidesheet.errors.notFound'),
        });
        return;
      }
      setError('code', {
        type: 'server',
        message: t('coupons.sidesheet.errors.notFound'),
      });
    },
  });

  const onSubmit = values => {
    const code = values.code.trim();
    if (!code) {
      setError('code', { type: 'validate', message: t('coupons.sidesheet.errors.required') });
      return;
    }

    addCouponMutation.mutate({ code });
  };

  const { data: stats } = useQuery({
    queryKey: ['user', 'coupon-stats', locale, selectedStatus],
    queryFn: () => getCouponsStats(user.token),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    notifyOnChangeProps: ['data'],
    enabled: !!user?.token,
  });

  const selectCoupons = useCallback(
    response => {
      if (!response.data) return [];
      return response.data
        .filter(coupon => coupon.status === selectedStatus)
        .map(coupon => {
          const daysLeft = daysUntilExpires(coupon.expiresAt);
          const isExpiringSoon = daysLeft >= 0 && daysLeft < 3;
          const isExpiringToday = daysLeft === 0;
          const isNew = coupon.code === newlyAddedCode;
          let open = undefined;
          let closed = undefined;
          let closingSoon = undefined;
          if (coupon.status === 'active' && isNew) {
            open = t('coupons.sidesheet.new_coupon');
            closingSoon = undefined;
          }
          if (coupon.status === 'active' && !isNew) {
            closingSoon = isExpiringToday
              ? t('coupons.expires')
              : isExpiringSoon && daysLeft !== null
              ? `${t('coupons.expire_in')} ${daysLeft} ${t('coupons.expire_in_days', { count: daysLeft })}`
              : undefined;
          }

          if (coupon.status === 'redeemed' && !isNew) {
            open = t('coupons.redeemed', { count: 1 });
          }

          if (coupon.status === 'expired' && !isNew) {
            closed = t('coupons.expired', { count: 1 });
          }

          return {
            _id: coupon._id,
            title: coupon.name,
            subtitle: formatExpiresDate(coupon.expiresAt, t('coupons.no_expiration')),
            description: coupon.description,
            code: { text: coupon.code, status: 'success' },
            status: isNew ? statusesMap['redeemed'] : statusesMap[coupon.status],
            openStoreLabelWording: open,
            closedStoreLabelWording: closed,
            closingSoonStoreLabelWording: closingSoon,
            isDisabled: coupon.status === 'redeemed' || coupon.status === 'expired',
          };
        });
    },
    [selectedStatus, newlyAddedCode, t]
  );

  const { data: coupons } = useQuery({
    queryKey: ['user', 'coupons', locale, selectedStatus],
    queryFn: () => getCoupons(user.token),
    enabled: !!user?.token,
    onError: console.log,
    select: selectCoupons,
  });

  const { data: totalRedeemedCoupons } = useQuery({
    queryKey: ['user', 'coupons', locale],
    queryFn: () => getCoupons(user.token),
    enabled: !!user?.token,
    onError: console.log,
    select: response => {
      if (!response.data) return [];
      return response.data.filter(coupon => coupon.status === 'redeemed').length;
    },
  });

  const onQrClick = qrCode => {
    setCode(qrCode);
    openModal();
  };

  const handleFilterChange = selectedItem => {
    setSelectedFilterId(selectedItem._id);
  };

  const submitDisabled = addCouponMutation.isPending || isSubmitting || !isValid;
  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.cardContainer}>
          <CouponsRedemptionCard
            currentBalanceLabel={t('coupons.total_savings')}
            orders={totalRedeemedCoupons || 0}
            postfix={symbol}
            currencyFormatter={formatter}
            pointsValue={stats?.data?.totalDiscountEarned / 100 || 0}
            totalOrdersLabel={t('coupons.coupons_redeemed')}
          />
        </div>
        <article className={styles.mainContainer}>
          <div className={styles.titleSection}>
            <Heading type={HeadingEnum.HeadingM} bold={BoldEnum.Bold}>
              {t('coupons.title')}
            </Heading>
            <Button
              className={styles.buttonStyles}
              variant="tonal"
              text={t('coupons.add')}
              onClick={() => setIsAddCouponSideSheetOpen(true)}
            />
          </div>

          <div className={styles.filterPills}>
            <FilterChipsList className={styles.filterChipsList} items={filters} onChange={handleFilterChange} />
          </div>

          <div className={styles.couponSection}>
            {coupons?.length ? (
              coupons.map((coupon, index) => (
                <div key={coupon?._id || `couponId${index}`} className={styles.couponOuterContainer}>
                  <Coupon
                    key={`${coupon._id}-${selectedStatus}`}
                    title={coupon.title}
                    subtitle={coupon.subtitle}
                    description={coupon.description}
                    code={coupon.code}
                    badge={coupon.badge}
                    onQrClick={code => onQrClick(code)}
                    onClickCoupon={code => onQrClick(code)}
                    status={coupon.status}
                    openStoreLabelWording={coupon.openStoreLabelWording}
                    closedStoreLabelWording={coupon.closedStoreLabelWording}
                    closingSoonStoreLabelWording={coupon.closingSoonStoreLabelWording}
                    isDisabled={coupon.isDisabled}
                  />
                </div>
              ))
            ) : (
              <div className={styles.emptyState}>
                <Image
                  size={ImageSizeEnum.CartEmptyState}
                  imgSrc={emptyStateImageMap[selectedStatus]}
                  imgTitle={'empty-state'}
                  alt={'Empty State'}
                />
                <Text type={TextEnum.Assistive} color={TextColorEnum.OnSurfaceLowEmphasis} bold={BoldEnum.Bold}>
                  {emptyStateTextMap[selectedStatus]}
                </Text>
                <Text type={TextEnum.Assistive} color={TextColorEnum.OnSurfaceLowEmphasis}>
                  {emptyStateSubTextMap[selectedStatus]}
                </Text>
              </div>
            )}
          </div>
        </article>
      </div>
      <ReactModal isOpen={modalIsOpen} onRequestClose={closeModal} style={customStyles} contentLabel="QR Code Modal">
        <Heading type={HeadingEnum.HeadingXS} bold={BoldEnum.Bold} classOption={styles.qrTitle}>
          {t('coupons.scan')}
        </Heading>
        <QRCodeSVG value={code} size={200} />
      </ReactModal>
      {/* {showQr && (
        <div style={{ marginTop: 16 }}>
          <QRCodeSVG value={code} size={160} />
        </div>
      )} */}
      {isAddCouponSidesheetOpen ? (
        <SideSheet
          header={t('coupons.sidesheet.title')}
          hasBack={false}
          hasClose
          position="right"
          onClose={() => setIsAddCouponSideSheetOpen(false)}
          onSubmit={handleSubmit(onSubmit)}
          buttonText={t('coupons.add')}
          isDisabled={submitDisabled}
        >
          <div className={styles.sideSheet}>
            <Heading type={HeadingEnum.HeadingS} bold={BoldEnum.Bold}>
              {t('coupons.sidesheet.label')}
            </Heading>

            <Controller
              control={control}
              name="code"
              rules={{
                required: t('coupons.sidesheet.errors.required'),
                validate: value => value.trim().length > 0 || t('coupons.sidesheet.errors.required'),
              }}
              render={({ field }) => (
                <div className={styles.inputContainer}>
                  <Input
                    {...field}
                    value={field.value}
                    onChange={field.onChange}
                    name={field.name}
                    helperTextType={errors?.code ? 'error' : undefined}
                    errorText={errors.code ? errors.code?.message : ''}
                    label={t('coupons.coupon_code')}
                  />
                </div>
              )}
            />
            <div className={styles.imageContainer}>
              <Image
                size={ImageSizeEnum.ContentCardShaped}
                imgSrc={CouponAvocado.src}
                imgTitle={'empty-state'}
                alt={'Empty State'}
              />
            </div>
          </div>
        </SideSheet>
      ) : null}
    </>
  );
}
